import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2, Mail, MessageCircle, Plus, RefreshCw, Send } from 'lucide-react';
import { useApi } from '@/hooks/useApi';
import { api } from '@/lib/api';
import { classNames, formatDateTime } from '@/lib/utils';
import { ESTADO_CAMPANA, canalDe, porcentaje, type Campana, type Canal } from '@/lib/comunicaciones';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';

/**
 * Campañas de correo. Un clic en una abre el editor si es un borrador, o su
 * informe si ya salió. Las cifras de cada fila se calculan al pedir la lista,
 * así que las aperturas que lleguen días después también se ven aquí.
 */
export default function CampanasPage() {
  const navigate = useNavigate();
  const { data, loading, error, refresh } = useApi(() => api.getCampanas(), []);
  const [creando, setCreando] = useState<Canal | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);

  const nueva = async (canal: Canal) => {
    setCreando(canal);
    setFallo(null);
    try {
      const c = await api.crearCampana(canal);
      navigate(`/comunicaciones/campanas/${c.id}`);
    } catch (err) {
      setFallo(err instanceof Error ? err.message : 'No se pudo crear la campaña');
      setCreando(null);
    }
  };

  const campanas = data || [];

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
        <div className="flex items-center gap-2">
          <Send size={24} className="text-sereno-dark shrink-0" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Campañas</h1>
            <p className="text-sm text-sereno-dark/40 mt-0.5">
              Correo (desde news.tusereno.es) y WhatsApp a suscriptores y propietarios, con baja en un clic y seguimiento.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={refresh} disabled={loading} className="btn-ghost flex items-center gap-2 text-sm" title="Recargar">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            <span className="hidden sm:inline">Actualizar</span>
          </button>
          <button onClick={() => nueva('email')} disabled={!!creando} className="btn-primary flex items-center gap-2 text-sm" title="Nueva campaña de correo">
            {creando === 'email' ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            <Mail size={16} /> Correo
          </button>
          <button onClick={() => nueva('whatsapp')} disabled={!!creando} className="btn-primary flex items-center gap-2 text-sm" title="Nueva campaña de WhatsApp">
            {creando === 'whatsapp' ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            <MessageCircle size={16} /> WhatsApp
          </button>
        </div>
      </div>

      {(error || fallo) && (
        <div className="card mb-4 flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle size={16} /> {fallo || error}
        </div>
      )}

      {loading && !data ? (
        <LoadingSpinner />
      ) : campanas.length === 0 ? (
        <EmptyState
          title="Todavía no hay campañas"
          description="Crea la primera: eliges a quién, montas el correo con bloques y lo pruebas antes de mandarlo."
          action={<button onClick={() => nueva('email')} className="btn-primary text-sm">Nueva campaña de correo</button>}
        />
      ) : (
        <>
          {/* Escritorio */}
          <div className="card p-0 overflow-hidden hidden md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-sereno-dark/40 border-b border-gray-100">
                  <th className="px-4 py-3 font-medium">Campaña</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium text-right">Destinatarios</th>
                  <th className="px-4 py-3 font-medium text-right">Entregados</th>
                  <th className="px-4 py-3 font-medium text-right" title="Correo: aperturas · WhatsApp: leídos">Abiertos</th>
                  <th className="px-4 py-3 font-medium text-right" title="Correo: clics · WhatsApp: respuestas">Clics / resp.</th>
                  <th className="px-4 py-3 font-medium text-right">Bajas</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {campanas.map(c => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/comunicaciones/campanas/${c.id}`)}
                    className="cursor-pointer hover:bg-slate-50/70"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-sereno-dark flex items-center gap-1.5"><IconoCanal c={c} /> {c.nombre}</div>
                      <div className="text-xs text-sereno-dark/40 mt-0.5">{fechaDe(c)}</div>
                    </td>
                    <td className="px-4 py-3"><EstadoCampana c={c} /></td>
                    <td className="px-4 py-3 text-right tabular-nums">{c.resumen?.total ?? '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{c.resumen ? porcentaje(c.resumen.entregados, c.resumen.enviados) : '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{c.resumen ? porcentaje(abiertos(c), c.resumen.enviados) : '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{c.resumen ? (canalDe(c) === 'whatsapp' ? c.resumen.respuestas || 0 : porcentaje(c.resumen.clics, c.resumen.enviados)) : '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{c.resumen ? c.resumen.bajas : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Móvil */}
          <div className="md:hidden space-y-3">
            {campanas.map(c => (
              <button
                key={c.id}
                onClick={() => navigate(`/comunicaciones/campanas/${c.id}`)}
                className="card p-4 w-full text-left"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium text-sereno-dark flex items-center gap-1.5"><IconoCanal c={c} /> {c.nombre}</div>
                  <EstadoCampana c={c} />
                </div>
                <div className="text-xs text-sereno-dark/40 mt-1">{fechaDe(c)}</div>
                {c.resumen && (
                  <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                    <Cifra etiqueta="Enviados" valor={String(c.resumen.enviados)} />
                    <Cifra etiqueta={canalDe(c) === 'whatsapp' ? 'Leídos' : 'Aperturas'} valor={porcentaje(abiertos(c), c.resumen.enviados)} />
                    <Cifra etiqueta={canalDe(c) === 'whatsapp' ? 'Respuestas' : 'Clics'} valor={canalDe(c) === 'whatsapp' ? String(c.resumen.respuestas || 0) : porcentaje(c.resumen.clics, c.resumen.enviados)} />
                  </div>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function EstadoCampana({ c }: { c: Pick<Campana, 'estado'> }) {
  const e = ESTADO_CAMPANA[c.estado];
  return <span className={classNames('badge', e.clase)}>{e.texto}</span>;
}

/** En WhatsApp, «abierto» es leído (el doble check azul), que es fiable; en correo, la apertura. */
function abiertos(c: Campana): number {
  return canalDe(c) === 'whatsapp' ? c.resumen?.leidos || 0 : c.resumen?.abiertos || 0;
}

function IconoCanal({ c }: { c: Campana }) {
  return canalDe(c) === 'whatsapp'
    ? <MessageCircle size={14} className="text-emerald-600 shrink-0" aria-label="WhatsApp" />
    : <Mail size={14} className="text-sereno-dark/40 shrink-0" aria-label="Correo" />;
}

function fechaDe(c: Campana): string {
  if (c.enviadaEn) return `Enviada el ${formatDateTime(c.enviadaEn)}`;
  if (c.lanzada) return `Empezó a salir el ${formatDateTime(c.lanzada.en)}`;
  if (c.programadaPara) return `Programada para el ${formatDateTime(c.programadaPara)}`;
  return `Editada el ${formatDateTime(c.actualizadaEn)}`;
}

function Cifra({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div>
      <div className="text-sereno-dark/40">{etiqueta}</div>
      <div className="font-semibold text-sereno-dark tabular-nums">{valor}</div>
    </div>
  );
}
